import React, { useState } from 'react';
import ExpandedReactions from './ExpandedReactions';
import { CommentSection } from './CommentSection';

interface FeedPostProps {
  post: {
    id: string;
    author: {
      name: string;
      avatar: string;
      level: number;
    };
    achievement: {
      type: 'badge' | 'level' | 'mission' | 'reward' | 'victory';
      title: string;
      description: string;
      image?: string;
      icon?: string;
    };
    timestamp: string;
    reactions: any[];
    comments: any[];
  };
  onShare?: () => void;
}

export const FeedPost: React.FC<FeedPostProps> = ({ post, onShare }) => {
  const [showComments, setShowComments] = useState(false);

  const achievementIcons = {
    badge: 'ri-medal-line',
    level: 'ri-arrow-up-circle-line',
    mission: 'ri-flag-line',
    reward: 'ri-gift-line',
    victory: 'ri-trophy-line'
  };

  const achievementColors = {
    badge: 'from-yellow-400 to-orange-500',
    level: 'from-blue-400 to-purple-500',
    mission: 'from-green-400 to-teal-500',
    reward: 'from-pink-400 to-rose-500',
    victory: 'from-amber-400 to-yellow-500'
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header do Post */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={post.author.avatar}
              alt={post.author.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-teal-500"
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold border-2 border-white">
              {post.author.level}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{post.author.name}</h3>
            <p className="text-xs text-gray-500">{post.timestamp}</p>
          </div>
        </div>
        <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
          <i className="ri-more-fill text-xl"></i>
        </button>
      </div>

      {/* Conteúdo da Conquista */}
      <div className={`bg-gradient-to-br ${achievementColors[post.achievement.type]} p-6 text-white`}>
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            {post.achievement.image ? (
              <img
                src={post.achievement.image}
                alt={post.achievement.title}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <i className={`${post.achievement.icon || achievementIcons[post.achievement.type]} text-3xl`}></i>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${achievementIcons[post.achievement.type]} text-xl`}></i>
              <span className="text-sm font-medium opacity-90">
                {post.achievement.type === 'badge' && 'Distintivo Desbloqueado'}
                {post.achievement.type === 'level' && 'Novo Nível Alcançado'}
                {post.achievement.type === 'mission' && 'Missão Completada'}
                {post.achievement.type === 'reward' && 'Recompensa Conquistada'}
                {post.achievement.type === 'victory' && 'Vitória Conquistada'}
              </span>
            </div>
            <h4 className="text-xl font-bold mb-2">{post.achievement.title}</h4>
            <p className="text-white/90 text-sm">{post.achievement.description}</p>
          </div>
        </div>
      </div>

      {/* Reações Expandidas */}
      <div className="p-4 border-t border-gray-200">
        <ExpandedReactions postId={post.id} />
      </div>

      {/* Ações */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-chat-3-line text-lg"></i>
            <span className="text-sm font-medium">
              {post.comments.length > 0 ? `${post.comments.length} comentários` : 'Comentar'}
            </span>
          </button>
          <button
            onClick={onShare}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-share-line text-lg"></i>
            <span className="text-sm font-medium">Compartilhar</span>
          </button>
        </div>

        {/* Seção de Comentários */}
        {showComments && (
          <div className="pt-4 border-t border-gray-200 mt-4">
            <CommentSection postId={post.id} initialComments={post.comments} />
          </div>
        )}
      </div>
    </div>
  );
};
